export type BoardOrientation = 'white' | 'black';

/** Stockfish UCI scores are always from White's perspective; flip for Black-at-bottom. */
export function flipEvalScore(score: EvalScore): EvalScore {
  return { kind: score.kind, value: -score.value };
}

/** Label + bar geometry aligned with the side at the bottom of the board. */
export function evalBarPresentation(
  score: EvalScore,
  orientation: BoardOrientation
): { displayScore: EvalScore; heightPct: number; fillFromTop: boolean } {
  if (orientation === 'white') {
    return {
      displayScore: score,
      heightPct: scoreToBarPercent(score),
      fillFromTop: false,
    };
  }
  return {
    displayScore: flipEvalScore(score),
    heightPct: 100 - scoreToBarPercent(score),
    fillFromTop: true,
  };
}

/** Map centipawns (White's perspective) to eval bar height % (White from bottom). */
export function cpToBarPercent(cp: number): number {
  const clamped = Math.max(-800, Math.min(800, cp));
  const sigmoid = 1 / (1 + Math.exp(-0.004 * clamped));
  return Math.round(8 + sigmoid * 84);
}

export type EvalScore =
  | { kind: 'cp'; value: number }
  | { kind: 'mate'; value: number };

export function parseUciScoreLine(line: string): EvalScore | null {
  const mate = /score mate (-?\d+)/.exec(line);
  if (mate) return { kind: 'mate', value: Number(mate[1]) };
  const cp = /score cp (-?\d+)/.exec(line);
  if (cp) return { kind: 'cp', value: Number(cp[1]) };
  return null;
}

/** Display text for the analysis bar. */
export function formatEvalScore(score: EvalScore): string {
  if (score.kind === 'mate') {
    const n = Math.abs(score.value);
    return score.value > 0 ? `M${n}` : `-M${n}`;
  }
  const pawns = score.value / 100;
  if (pawns > 0) return `+${pawns.toFixed(1)}`;
  if (pawns < 0) return pawns.toFixed(1);
  return '0.0';
}

/** Bar height % from mate (positive = White mates). */
export function mateToBarPercent(mate: number): number {
  if (mate > 0) return 96;
  if (mate < 0) return 4;
  return 50;
}

export function scoreToBarPercent(score: EvalScore): number {
  if (score.kind === 'mate') return mateToBarPercent(score.value);
  return cpToBarPercent(score.value);
}
